import { uid } from "@dhx/ts-common/core";
import { types } from "./configurator/Configurator";
import { getFilterHandler } from "./filters/Filters";
import { groupMethods, strToDate } from "./helpers";
import {
	IDataPreprocessor,
	IField,
	IFilterInfo,
	IFilterMeta,
	IParsedPivotConfig,
	IPivotConfig,
	IPivotData,
	IPivotFields,
	IRawPivotFields,
} from "./types";

export class DataPreprocessor implements IDataPreprocessor {
	private _filters: { [key: string]: IFilterInfo };
	private _subFields: any = {};
	private _globalFilter: any;
	private _fields: { [key: string]: IField };

	constructor() {
		this._filters = {};
		this._fields = {};
	}

	setFilterValue(fieldId: string, meta: IFilterMeta) {
		const field = this._fields[fieldId];
		if (!meta || !meta.values || meta.values.length <= 0) {
			delete this._filters[fieldId];
			field.$filter = false;
		} else {
			this._filters[fieldId] = {
				meta,
				handler: getFilterHandler(meta, field),
			};
			field.$filter = true;
		}
	}

	setFiltersValues(filtersMeta: any) {
		for (const field in filtersMeta) {
			if (!this.getFilterValue(field)) {
				this.setFilterValue(field, filtersMeta[field]);
			}
		}
	}

	getFilterValue(fieldId: string): IFilterMeta {
		return this._filters[fieldId] ? this._filters[fieldId].meta : null;
	}

	getFiltersValues() {
		const filters = {};
		for (const key in this._filters) {
			filters[key] = this._filters[key].meta;
		}
		return filters as any;
	}

	clearFiltersValues() {
		this._filters = {};
	}

	setGlobalFilter(handler?: any) {
		this._globalFilter = handler || null;
	}

	addSubField(name: string, functor: any, label: string) {
		this._subFields[name] = this._subFields[name] || {};
		this._subFields[name][label] = functor;

		groupMethods[label] = functor;

		types[name] = types[name] || [];
		types[name].push({ id: label, label, method: functor });
	}

	normalizeConfig(config: IPivotConfig): IParsedPivotConfig {
		this.normalizeFields(config);
		config.layout = config.layout || {};
		config.layout.fieldSelectorType = config.layout.fieldSelectorType || "dropdown";
		config.layout.gridMode = config.layout.gridMode || "tree";

		config.types = config.types || {};

		return config as IParsedPivotConfig;
	}

	normalizeData(data: IPivotData, fieldList: any): any[] {
		data = this._parseAliases(data, fieldList);
		this._parseDates(data, fieldList);

		return data as any[];
	}

	normalizeFields(config: any) {
		if (!config.fields) {
			config.fields = {
				columns: [],
				rows: [],
				values: [],
			};
		}

		config.fields.free = [];
		const idMap = {};

		for (const key in config.fields) {
			config.fields[key] = config.fields[key].map(el => {
				el = typeof el === "string" ? { id: el } : el;

				const elFromList = config.fieldList.filter(e => e.id === el.id)[0];
				el = { ...elFromList, ...el, $field: key, $uid: uid() };

				if (key === "values") {
					el.$temp = true;
				} else {
					idMap[el.id] = true;
				}
				return el;
			});
		}
		config.fields.free = config.fieldList
			.filter(el => !idMap[el.id])
			.map(el => {
				el = { ...el, $field: "free", $uid: uid() };
				if (el.type === "date") {
					el.group = el.group || "dateByDay";
				}
				return el;
			});
		this._fields = config.fieldList.reduce((obj, f) => {
			obj[f.id] = f;
			return obj;
		}, {});
	}

	getData(data: any[] = [], fields: IPivotFields): any[] {
		return this._checkData(data, fields);
	}

	getPureFields(fields: IPivotFields) {
		const f = {
			columns: [...fields.columns],
			rows: [...fields.rows],
			values: [...fields.values],
		};
		for (const key in f) {
			f[key] = f[key].map(el => {
				if (el.method) {
					return { id: el.id, method: el.method };
				} else if (el.group) {
					return { id: el.id, group: el.group };
				} else {
					return el.id;
				}
			});
		}
		return f;
	}

	private _parseDates(data: any[], fieldList: any) {
		for (const item of fieldList) {
			if (item.type === "date") {
				types[item.id] = types.dates;
				item.group = item.group || "dateByDay";
				for (let i = 0; i < data.length; i++) {
					const current = data[i][item.id];
					data[i][item.id] = current !== null ? strToDate(current, item.format) : null;
				}
			}
		}
	}

	private _parseAliases(data: any, fieldList: any): any[] {
		if ("data" in data) {
			if (data.aliases) {
				for (const field of fieldList) {
					if (data.aliases[field.id]) {
						field.aliases = data.aliases[field.id];
					}
				}
			}
			data = data.data;
		}
		return this._checkAliases(data as any[], fieldList);
	}

	private _checkAliases(data: any[], fieldList: any): any[] {
		return data.map(el => {
			for (const field of fieldList) {
				if (field.aliases && field.aliases[el[field.id]]) {
					el[field.id] = field.aliases[el[field.id]];
				}
			}
			return el;
		});
	}

	private _checkData(data: any[], fields: IRawPivotFields): any[] {
		let checkedData = [...data];
		if (this._globalFilter) {
			checkedData = checkedData.filter(row => this._globalFilter(row));
		}
		if (Object.keys(this._filters).length) {
			for (const key in this._filters) {
				const filter = this._filters[key];
				checkedData = checkedData.filter(item => filter.handler(item[key]));
			}
		}

		for (const key in fields) {
			for (const obj of fields[key]) {
				if (obj.group && !obj.method) {
					checkedData = this._callGroupMethod(checkedData, obj);
				}
			}
		}
		return checkedData;
	}

	private _callGroupMethod(data: any[], field: IField) {
		if (groupMethods[field.group]) {
			return data.map(el => {
				el = { ...el };
				if (el[field.id] !== null) {
					el[field.id] = groupMethods[field.group](el[field.id]);
				}
				return el;
			});
		} else {
			return data;
		}
	}
}
